import React, { Component } from 'react';
import moment, { Moment } from 'moment';
import DailyAveragesBreakdown from '../elements/reportParts/dailyAveragesBreakdown';
import DogCountBreakdown from '../elements/reportParts/dogCountBreakdown';
import GeneralDataBreakdown from '../elements/reportParts/generalBreakdown';
import HumanContributionBreakdown from '../elements/reportParts/humanContributionBreakdown';
import IndividualDogBreakdown from '../elements/reportParts/individualDogBreakdown';
import IndividualHumanBreakdown from '../elements/reportParts/individualHumanBreakdown';
import ParticipationBreakdown from '../elements/reportParts/participationBreakdown';
import ReportTitle from '../elements/reportParts/reportTitle';
import {
  getTotalEnrichmentTime,
  getNRandomColors,
  getEnrichmentTypes,
  breakdownHumanContributions,
  retrieveUsedColors,
  seperateAnimalsByColorAndDate,
  countUniqueAnumbersByColor,
  prepForArea,
  prepColorsForArea,
  getAverageForSingleEnrichment,
  getAverageForDailyEnrichment,
  getAveragesForEachDayForChart,
  animalsByDay,
  countUniqueAnumbers,
  getAverageAnimalsInActivityPerDay,
  getActivityParticipationDataForChart,
  getTotalsForAllEnrichmentTypesForEachAnimal,
  getHumanTimeBreakdown,
} from '../util/databaseFunctions';
import { errorAlert } from '../util/helperFunctions';
// @ts-ignore
import Worker from 'worker-loader!../util/reports.worker'; // eslint-disable-line
import { Box } from 'bloomer/lib/elements/Box';
import { Content } from 'bloomer/lib/elements/Content';
import LoadingSpinner from '../elements/LoadingSpinner';
import { Progress } from 'bloomer/lib/elements/Progress';

/* tslint:disable:object-shorthand-properties-first*/

interface PropTypes {
  startDate: Moment;
  endDate: Moment;
  enrichmentData: Enrichment[];
  animalType: animalType | '';
}

interface StateTypes {
  loading: boolean;
  loadingPercent: number | false;
  loadingMessage: string;
  totalEnrichmentTime: any;
  enrichmentTypes: any;
  colors: any;
  animalsByDate: any;
  animalCountArray: any;
  animalCountChart: any;
  animalCountAverage: any;
  colorCode__animalsByDate: any;
  colorCode__animalCountArray: any;
  colorCode__animalCountChart: any;
  possibleColors: any;
  averageSingleEnrichment: any;
  averageDailyEnrichment: any;
  averagesDailyForArea: any;
  longStay__averageSingleEnrichment: any;
  longStay__averageDailyEnrichment: any;
  longStay__averagesDailyForArea: any;
  behaviour__averageSingleEnrichment: any;
  behaviour__averageDailyEnrichment: any;
  behaviour__averagesDailyForArea: any;
  averageDogsInOfficeEachDay: any;
  averageDogsInPlaygroupEachDay: any;
  officeParticipationData: any;
  playgroupParticipationData: any;
  humanEffort: any;
  humanEffortAcc: any;
  animalDataSums: any;
  humanData: any;
}

class ReportContent extends Component<PropTypes, StateTypes> {
  worker: Worker | undefined;
  interval: number | undefined;
  state: StateTypes = {
    loading: true,
    totalEnrichmentTime: 0,
    enrichmentTypes: [],
    colors: getNRandomColors(10),
    animalsByDate: {},
    animalCountArray: [],
    animalCountChart: [],
    animalCountAverage: 0,
    colorCode__animalsByDate: {},
    colorCode__animalCountArray: {},
    colorCode__animalCountChart: [],
    possibleColors: [],
    averageSingleEnrichment: 0,
    averageDailyEnrichment: 0,
    averagesDailyForArea: [],
    longStay__averageSingleEnrichment: 0,
    longStay__averageDailyEnrichment: 0,
    longStay__averagesDailyForArea: [],
    behaviour__averageSingleEnrichment: 0,
    behaviour__averageDailyEnrichment: 0,
    behaviour__averagesDailyForArea: [],
    averageDogsInOfficeEachDay: 0,
    averageDogsInPlaygroupEachDay: 0,
    officeParticipationData: [],
    playgroupParticipationData: [],
    humanEffort: [],
    humanEffortAcc: [],
    animalDataSums: [],
    humanData: [],
    loadingPercent: 0,
    loadingMessage: 'Initializing...',
  };

  handleWorkerMessage = (response: { type: string; value: any }) => {
    if (response.type === 'percent') {
      this.updatePercent(response.value);
    } else if (response.type === 'state') {
      this.setState(response.value, () =>
        this.setState({
          loading: false,
          loadingPercent: 0,
          loadingMessage: 'Initializing...',
        }),
      );
    } else if (response.type === 'message') {
      this.updateMessage(response.value);
    } else if (response.type === 'error') {
      errorAlert(response.value);
    }
  };

  webWorkerInit = () => {
    const worker = new Worker();
    this.worker = worker;
    const { startDate, endDate } = this.props;
    const data = this.props.enrichmentData;
    worker.onmessage = e => {
      this.handleWorkerMessage(JSON.parse(e.data));
    };
    worker.postMessage(
      JSON.stringify({
        startDate,
        endDate,
        data,
      }),
    );
  };

  processData = () => {
    const startDate = this.props.startDate.toISOString();
    const endDate = this.props.startDate.toISOString();

    const data = this.props.enrichmentData;
    /* -- Dog Count -- */
    // sort animals by date
    const animalsByDate = animalsByDay(startDate, endDate, data);
    this.updatePercent(50);
    // count them
    const animalCountArray = Object.values(animalsByDate).map(date => countUniqueAnumbers(date));

    // averageThem
    const average =
      animalCountArray.reduce((accumulator, current) => accumulator + current, 0) / animalCountArray.length;

    // breakdown our data by color, then by date
    const animalsByColorThenDate = seperateAnimalsByColorAndDate(startDate, endDate, data);

    // count them
    const colorAnimalCountArrays = countUniqueAnumbersByColor(animalsByColorThenDate);

    // format data for our recharts components
    const animalCountChartArray = prepForArea(animalCountArray);
    const animalsByColorCodeChartArray = prepColorsForArea(startDate, endDate, colorAnimalCountArrays);

    // get all colors used in our data
    const possibleColors = retrieveUsedColors(data);

    const enrichmentTypes = getEnrichmentTypes(this.props.enrichmentData);

    const averageSingleEnrichment = getAverageForSingleEnrichment(data);
    const averageDailyEnrichment = getAverageForDailyEnrichment(startDate, endDate, data);
    const averagesDailyForArea = getAveragesForEachDayForChart(startDate, endDate, data);
    const longStay__averageSingleEnrichment = getAverageForSingleEnrichment(data, 'longstay');
    const longStay__averageDailyEnrichment = getAverageForDailyEnrichment(startDate, endDate, data, 'longstay');
    const longStay__averagesDailyForArea = getAveragesForEachDayForChart(startDate, endDate, data, 'longstay');
    const behaviour__averageSingleEnrichment = getAverageForSingleEnrichment(data, 'behaviour');
    const behaviour__averageDailyEnrichment = getAverageForDailyEnrichment(startDate, endDate, data, 'behaviour');
    const behaviour__averagesDailyForArea = getAveragesForEachDayForChart(startDate, endDate, data, 'behaviour');
    const averageDogsInOfficeEachDay = getAverageAnimalsInActivityPerDay(startDate, endDate, data, 'office');
    const averageDogsInPlaygroupEachDay = getAverageAnimalsInActivityPerDay(startDate, endDate, data, 'playgroup');
    const officeParticipationData = getActivityParticipationDataForChart(startDate, endDate, data, 'office');
    const playgroupParticipationData = getActivityParticipationDataForChart(startDate, endDate, data, 'playgroup');
    const humanEffort = breakdownHumanContributions(data, true);
    const humanEffortAcc = breakdownHumanContributions(data);
    const animalDataSums = getTotalsForAllEnrichmentTypesForEachAnimal(data, enrichmentTypes);
    const totalEnrichmentTime = getTotalEnrichmentTime(data);
    const humanData = getHumanTimeBreakdown(data);

    this.setState(
      {
        // animal count
        animalsByDate,
        animalCountArray,
        animalCountChart: animalCountChartArray,
        animalCountAverage: average,
        colorCode__animalsByDate: animalsByColorThenDate,
        colorCode__animalCountArray: colorAnimalCountArrays,
        colorCode__animalCountChart: animalsByColorCodeChartArray,
        possibleColors,
        // -- animal count end
        // -- dailyAveragesBreakdown
        averageSingleEnrichment,
        averageDailyEnrichment,
        averagesDailyForArea,
        longStay__averageSingleEnrichment,
        longStay__averageDailyEnrichment,
        longStay__averagesDailyForArea,
        behaviour__averageSingleEnrichment,
        behaviour__averageDailyEnrichment,
        behaviour__averagesDailyForArea,
        // -- dailyAveragesBreakdown end
        // -- ParticipationBreakdown
        averageDogsInOfficeEachDay,
        averageDogsInPlaygroupEachDay,
        officeParticipationData,
        playgroupParticipationData,
        // participationBreakdown end
        // human contribution breakdown
        humanEffort,
        humanEffortAcc,
        // human contribution breakdown end
        // individual animals
        animalDataSums,
        totalEnrichmentTime,
        enrichmentTypes,
        humanData,
      },
      () =>
        this.setState({
          loading: false,
          loadingPercent: 0,
          loadingMessage: 'Initializing...',
        }),
    );
  };

  componentDidMount() {
    if ((window as any).Worker) {
      this.webWorkerInit();
    } else {
      this.setState(
        {
          loadingMessage:
            'It looks like your browser does not support Web-Workers, so this might take a while. Sit tight!',
          loadingPercent: false,
        },
        () => {
          this.interval = setTimeout(() => {
            this.processData();
          }, 100);
        },
      );
    }
  }

  updateMessage = (message: string) => {
    this.setState({
      loadingMessage: message,
    });
  };

  updatePercent = (number: number) => {
    let num;
    if (number > 100) {
      num = 100;
    } else if (number < 0) {
      num = 0;
    } else {
      num = number;
    }
    this.setState({
      loadingPercent: num,
    });
  };

  render() {
    const startDate = moment(this.props.startDate).format('MMMM Do YYYY');
    const endDate = moment(this.props.endDate).format('MMMM Do YYYY');
    const {
      animalCountArray,
      animalCountChart,
      animalCountAverage,
      colorCode__animalsByDate,
      colorCode__animalCountArray,
      colorCode__animalCountChart,
      possibleColors,
      averageSingleEnrichment,
      averageDailyEnrichment,
      averagesDailyForArea,
      longStay__averageSingleEnrichment,
      longStay__averageDailyEnrichment,
      longStay__averagesDailyForArea,
      behaviour__averageSingleEnrichment,
      behaviour__averageDailyEnrichment,
      behaviour__averagesDailyForArea,
      averageDogsInOfficeEachDay,
      averageDogsInPlaygroupEachDay,
      officeParticipationData,
      playgroupParticipationData,
      humanEffort,
      humanEffortAcc,
      animalDataSums,
      humanData,
    } = this.state;
    const { animalType } = this.props;

    const { loadingMessage } = this.state;
    const percent = this.state.loadingPercent;

    if (this.state.loading) {
      return (
        <Box>
          <LoadingSpinner />
          <Progress isColor="primary" value={percent ? percent : 0} max={100} />
          <p>{loadingMessage}</p>
        </Box>
      );
    }
    return (
      <Content>
        <ReportTitle startDate={startDate} endDate={endDate} />
        <GeneralDataBreakdown
          totalTime={this.state.totalEnrichmentTime}
          startDate={startDate}
          endDate={endDate}
          data={this.props.enrichmentData}
          animalType={animalType}
        />
        <DogCountBreakdown
          startDate={startDate}
          endDate={endDate}
          data={this.props.enrichmentData}
          colors={this.state.colors}
          animalCountArray={animalCountArray}
          animalCountAverage={animalCountAverage}
          animalCountChart={animalCountChart}
          colorCode__animalCountArray={colorCode__animalCountArray}
          colorCode__animalsByDate={colorCode__animalsByDate}
          colorCode__animalCountChart={colorCode__animalCountChart}
          possibleColors={possibleColors}
          animalType={animalType}
        />
        <DailyAveragesBreakdown
          startDate={startDate}
          endDate={endDate}
          colors={this.state.colors}
          averageSingleEnrichment={averageSingleEnrichment}
          averageDailyEnrichment={averageDailyEnrichment}
          averagesDailyForArea={averagesDailyForArea}
          longStay__averageSingleEnrichment={longStay__averageSingleEnrichment}
          longStay__averageDailyEnrichment={longStay__averageDailyEnrichment}
          longStay__averagesDailyForArea={longStay__averagesDailyForArea}
          behaviour__averageSingleEnrichment={behaviour__averageSingleEnrichment}
          behaviour__averageDailyEnrichment={behaviour__averageDailyEnrichment}
          behaviour__averagesDailyForArea={behaviour__averagesDailyForArea}
          animalType={animalType}
        />
        {animalType === 'cat' || (
          <ParticipationBreakdown
            startDate={startDate}
            endDate={endDate}
            colors={this.state.colors}
            averageDogsInPlaygroupEachDay={averageDogsInPlaygroupEachDay}
            officeParticipationData={officeParticipationData}
            playgroupParticipationData={playgroupParticipationData}
            averageDogsInOfficeEachDay={averageDogsInOfficeEachDay}
          />
        )}
        <HumanContributionBreakdown
          startDate={startDate}
          endDate={endDate}
          totalTime={this.state.totalEnrichmentTime}
          humanEffort={humanEffort}
          humanEffortAcc={humanEffortAcc}
        />
        <IndividualDogBreakdown
          startDate={startDate}
          endDate={endDate}
          data={this.props.enrichmentData}
          enrichmentTypes={this.state.enrichmentTypes}
          animalDataSums={animalDataSums}
          animalType={animalType}
        />
        <IndividualHumanBreakdown data={this.props.enrichmentData} humanData={humanData} animalType={animalType} />
      </Content>
    );
  }

  componentWillUnmount() {
    if (this.worker) {
      this.worker.terminate();
    }
    if (this.interval) {
      clearTimeout(this.interval);
    }
  }
}

export default ReportContent;
